import { useMemo } from "react";

import { getTzDayjs } from "@m7-health/shared-utils";

import {
  IScheduleShiftType,
  Schedule,
  ShiftTypeHelpers,
  useListScheduleShiftTypeQuery,
  useListSchedulesQuery,
  useListUsersQuery,
  usePaginatedListEventLogsQuery,
} from "@/api";
import { NOT_EXISTING_UUID } from "@/common/constants";
import { useCurrentTimezone, useKeyBy, useMapBy, useUnitIdByScheduleId } from "@/common/hooks";

import { TTarget } from "./types";

const emptySchedules: Schedule.DTO[] = [];
const emptyShiftTypes: IScheduleShiftType[] = [];

export const useDataForShift = (target: TTarget, currentUnitId?: string) => {
  const currentTz = useCurrentTimezone();
  const { data: availableSchedules = emptySchedules } = useListSchedulesQuery(
    {
      unitIds: [currentUnitId || NOT_EXISTING_UUID],
      liveOnDate: target.date,
    },
    { skip: !currentUnitId },
  );

  const filters = useMemo(() => {
    const dateJs = getTzDayjs(target.date, currentTz);

    if (target.staffShiftId) {
      return { relatedIds: [target.staffShiftId] };
    }

    if (target.userId && target.date) {
      return {
        staffIds: [target.userId],
        relatedDates: [
          {
            operator: "gte" as const,
            value: dateJs.startOf("day").toISOString(),
          },
          {
            operator: "lte" as const,
            value: dateJs.endOf("day").toISOString(),
          },
        ],
      };
    }

    if (target.changeReportSidebarFilters) {
      const { dateOfChange, dateImpacted, ...remainingFilters } = target.changeReportSidebarFilters;
      return {
        ...remainingFilters,
        scheduleIds: remainingFilters.scheduleIds.length
          ? remainingFilters.scheduleIds
          : availableSchedules.map(({ id }) => id),
        relatedDates: dateImpacted
          ? [
              {
                operator: "gte" as const,
                value: dateImpacted.startOf("day").toISOString(),
              },
              {
                operator: "lte" as const,
                value: dateImpacted.endOf("day").toISOString(),
              },
            ]
          : undefined,
        emittedAt: dateOfChange
          ? [
              {
                operator: "gte" as const,
                value: dateOfChange.startOf("day").toISOString(),
              },
              {
                operator: "lte" as const,
                value: dateOfChange.endOf("day").toISOString(),
              },
            ]
          : undefined,
      };
    }

    return {};
  }, [
    target.date,
    target.staffShiftId,
    target.userId,
    target.changeReportSidebarFilters,
    currentTz,
    availableSchedules,
  ]);

  const { data: logs, isLoading } = usePaginatedListEventLogsQuery(filters || {}, {
    skip: !filters,
  });

  const availableScheduleIds = useMapBy(availableSchedules, "id");
  const { data: scheduleShiftTypes = emptyShiftTypes } = useListScheduleShiftTypeQuery(
    { scheduleIds: availableScheduleIds },
    { skip: !availableScheduleIds.length },
  );
  const indexedShiftTypes = useMemo(
    () => ShiftTypeHelpers.byScheduleIdByShiftTypeKey(scheduleShiftTypes),
    [scheduleShiftTypes],
  );

  const unitIdByScheduleId = useUnitIdByScheduleId();

  const { data: users = [] } = useListUsersQuery({});
  const usersById = useKeyBy(users, "id");

  return useMemo(
    () => ({
      logs: logs?.list,
      pagination: logs?.pagination,
      usersById,
      indexedShiftTypes,
      unitIdByScheduleId,
      isLoading,
      availableSchedules,
    }),
    [logs, usersById, indexedShiftTypes, unitIdByScheduleId, isLoading, availableSchedules],
  );
};
